// An original 16-second electro loop, synthesised in the page: nothing is recorded or
// downloaded. 128 BPM would not fit 16 s evenly, so it runs at 120 BPM: 8 bars of four-on-the-
// floor kick, claps, sixteenth hats, a side-chained saw bass, a filtered chord pad, a sixteenth
// arpeggio and a noise riser into the second half. Rendered once into a stereo 16-bit WAV
// (a blob: URL) that the hero document names as its audio asset, so the Pulse mixer plays it
// and its analysis drives the scene's audio widgets and bindings.

export const BPM = 120;
export const BARS = 8;
export const DURATION = (BARS * 4 * 60) / BPM; // 16 s
const RATE = 32000;

function rng(seed) {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const midi = (n) => 440 * 2 ** ((n - 69) / 12);

function wav(L, R) {
  const n = L.length;
  const buf = new ArrayBuffer(44 + n * 4);
  const v = new DataView(buf);
  const w = (o, s) => { for (let i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i)); };
  w(0, 'RIFF'); v.setUint32(4, 36 + n * 4, true); w(8, 'WAVE'); w(12, 'fmt ');
  v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 2, true);
  v.setUint32(24, RATE, true); v.setUint32(28, RATE * 4, true); v.setUint16(32, 4, true); v.setUint16(34, 16, true);
  w(36, 'data'); v.setUint32(40, n * 4, true);
  let o = 44;
  for (let i = 0; i < n; i++) {
    v.setInt16(o, Math.max(-1, Math.min(1, L[i])) * 32767, true);
    v.setInt16(o + 2, Math.max(-1, Math.min(1, R[i])) * 32767, true);
    o += 4;
  }
  return new Blob([buf], { type: 'audio/wav' });
}

let cached = null;

/** Render the loop once; returns {url, duration, bpm}. */
export function soundtrack() {
  if (cached) return cached;
  const n = Math.ceil(DURATION * RATE);
  const L = new Float32Array(n);
  const R = new Float32Array(n);
  const duck = new Float32Array(n).fill(1); // side-chain envelope from the kick
  const r = rng(11);
  const beat = 60 / BPM;
  const six = beat / 4;

  const add = (t0, len, fn, pan = 0, gain = 1) => {
    const a = Math.floor(t0 * RATE);
    const m = Math.floor(len * RATE);
    const gl = gain * Math.min(1, 1 - pan);
    const gr = gain * Math.min(1, 1 + pan);
    for (let i = 0; i < m; i++) {
      const j = (a + i) % n; // wraps, so tails of the last bar land in the first: a seamless loop
      const s = fn(i / RATE, i / m);
      L[j] += s * gl;
      R[j] += s * gr;
    }
  };

  // F minor: Fm - Db - Ab - Eb, two bars each.
  const roots = [41, 37, 44, 39];
  const chords = [[65, 68, 72], [61, 65, 68], [60, 63, 68], [58, 63, 67]];

  // Kick, and the side-chain envelope it drives.
  for (let b = 0; b < BARS * 4; b++) {
    const t = b * beat;
    add(t, 0.32, (s, u) => Math.sin(2 * Math.PI * (48 * s + 130 * (1 - Math.exp(-s * 38)) / 38)) * (1 - u) ** 1.6 * 0.95);
    const a = Math.floor(t * RATE);
    for (let i = 0; i < beat * RATE; i++) duck[(a + i) % n] = Math.min(duck[(a + i) % n], 0.25 + 0.75 * Math.min(1, i / (beat * RATE * 0.55)));
  }
  // Claps on 2 and 4 (three quick noise bursts, a little room tail).
  for (let b = 1; b < BARS * 4; b += 2) {
    const t = b * beat;
    add(t, 0.22, (s) => { const e = s < 0.03 ? (Math.floor(s * 100) % 1 ? 1 : 0.8) * (1 - (s * 100) % 1) : Math.exp(-(s - 0.03) * 22); return (r() * 2 - 1) * e * 0.34; }, -0.15);
  }
  // Hats: closed sixteenths with an accent pattern, open hats on the off-beats.
  const acc = [0.5, 0.2, 0.9, 0.25];
  for (let k = 0; k < BARS * 16; k++) {
    const t = k * six;
    let prev = 0;
    add(t, 0.04, (s, u) => { const x = r() * 2 - 1, h = x - prev; prev = x; return h * (1 - u) ** 3 * 0.16 * acc[k % 4]; }, 0.35);
    if (k % 4 === 2) { let p2 = 0; add(t, 0.18, (s, u) => { const x = r() * 2 - 1, h = x - p2; p2 = x; return h * (1 - u) ** 2 * 0.08; }, -0.3); }
  }
  // Bass: saw through a one-pole low-pass, octave-bouncing eighths, ducked by the kick.
  for (let k = 0; k < BARS * 8; k++) {
    const bar = Math.floor(k / 8);
    const note = roots[Math.floor(bar / 2) % 4] + (k % 2 ? 12 : 0);
    const f = midi(note);
    let lp = 0;
    const t0 = k * beat / 2;
    add(t0, beat / 2 * 0.92, (s, u) => {
      const saw = 2 * ((s * f) % 1) - 1;
      const cut = 0.05 + 0.25 * (1 - u) ** 2;
      lp += (saw - lp) * cut;
      return lp * Math.min(1, u * 40) * (1 - u) ** 0.4 * 0.5 * duck[Math.floor((t0 + s) * RATE) % n];
    });
  }
  // Pad: detuned saws per chord note, slow filter swell, wide stereo.
  for (let c = 0; c < BARS / 2; c++) {
    const t0 = c * 8 * beat;
    chords[c % 4].forEach((note, i) => {
      for (const det of [-0.12, 0.12]) {
        const f = midi(note + det);
        let lp = 0;
        add(t0, 8 * beat, (s, u) => {
          const saw = 2 * ((s * f) % 1) - 1;
          lp += (saw - lp) * (0.02 + 0.05 * Math.sin(Math.PI * u));
          return lp * Math.min(1, u * 12) * Math.min(1, (1 - u) * 20) * 0.075 * duck[Math.floor((t0 + s) * RATE) % n];
        }, det < 0 ? -0.6 + i * 0.1 : 0.6 - i * 0.1);
      }
    });
  }
  // Arpeggio from bar 3: chord tones up two octaves, square-ish pluck, ping-pong.
  const pat = [0, 1, 2, 1, 2, 0, 2, 1];
  for (let k = 32; k < BARS * 16; k++) {
    const bar = Math.floor(k / 16);
    const ch = chords[Math.floor(bar / 2) % 4];
    const note = ch[pat[k % 8]] + 12 + (k % 16 >= 12 ? 12 : 0);
    const f = midi(note);
    add(k * six, six * 1.6, (s, u) => {
      const ph = (s * f) % 1;
      return (ph < 0.3 ? 1 : -1) * Math.exp(-u * 5) * 0.055 + Math.sin(2 * Math.PI * f * 2 * s) * Math.exp(-u * 8) * 0.03;
    }, k % 2 ? 0.45 : -0.45);
  }
  // Riser into bar 5 and a crash on its downbeat.
  add(12 * beat, 4 * beat, (s, u) => (r() * 2 - 1) * u ** 2.5 * 0.18 + Math.sin(2 * Math.PI * (200 + 1400 * u * u) * s) * u ** 3 * 0.05, 0);
  add(16 * beat, 2.5, (s, u) => (r() * 2 - 1) * (1 - u) ** 3 * 0.2, 0.2);

  // Soft clip and normalise.
  let peak = 0;
  for (let i = 0; i < n; i++) {
    L[i] = Math.tanh(L[i] * 1.2);
    R[i] = Math.tanh(R[i] * 1.2);
    peak = Math.max(peak, Math.abs(L[i]), Math.abs(R[i]));
  }
  const g = 0.89 / (peak || 1);
  for (let i = 0; i < n; i++) { L[i] *= g; R[i] *= g; }

  cached = { url: URL.createObjectURL(wav(L, R)), duration: DURATION, bpm: BPM };
  return cached;
}
